"use client";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Search, Notifications, LocationOn, Verified, EmojiEvents, Business, School, CameraAltRounded } from "@mui/icons-material";
// import Image from 'next/image'
import { useUserProfileContext } from "./Layout";
import { useUser } from "@/contexts/UserContext";
import { useEffect, useState } from "react";
import { PaperclipIcon } from "lucide-react";
import { sanitizeInputName } from "@/utils/sanitizeInput";
import { validateEmail, validateFirstnameLastname } from "@/utils/validation";
import SelectWithSearch from "@/components/SelectWithSearch";
import { apiRemote, ICity, ICountry, IUserProfile, useApiCache } from "@/api";
import { useAvatar } from "@/contexts/AvatarContext";
import { useAlert } from "@/contexts/AlertContext";
import UserFlowModal from "./components/userFlowModal";
import { useNavigate } from "react-router";

const generateYearOptions = (): string[] => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 1940; year <= currentYear; year++) {
    years.push(year.toString());
  }
  return years;
};

export default function UserProfile() {
  var { user } = useUser();
  const { avatar, setAvatar } = useAvatar();
  const alert = useAlert();
  const [bpUploaderNonce, setBpUploaderNonce] = useState<string>("");
  const [bpAvatarCropstoreNonce, setBpAvatarCropstoreNonce] = useState<string>("");
  const [bpDeleteAvatarLinkNonce, setBpDeleteAvatarLinkNonce] = useState<string>("");
  const [hasAvatar, setHasAvatar] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0)
  const navigate = useNavigate();
  const [profile, setProfile] = useState({} as any);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true); // Bắt đầu tải dữ liệu
    try {
      const res = await apiRemote.getEducatorInfo(user?.user_login);
      setProfile(res.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const validExtensions = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validExtensions.includes(file.type)) {
        alert.error('Only accept files in .jpg, .jpeg, .png formats');
        return;
      }
    }

    if (!file) return;

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    if (file && file.size > maxFileSize) {
      alert.error('File size is too large. Please select a file smaller than 5MB.');
      event.target.value = ''; // Clear the input
      return;
    }
    
    try {
      const bp_params = {
        object: 'user',
        item_id: user?.ID,
        has_avatar: hasAvatar,
        nonces: {
          set: bpAvatarCropstoreNonce,
          remove: bpDeleteAvatarLinkNonce
        },
        ui_available_width: 788
      };
      const action = 'bp_avatar_upload';
      const fileName = file.name;
      apiRemote
        .uploadAvatar({
          avatar: file,
          nonce: bpUploaderNonce,
          bp_params,
          action,
          name: file.name,
        })
        .then((res) => {
          // alert.success(res.update_message);
          if (res.data.avatar != "") {
            setAvatar(res.data.avatar);
          }
        })
        .catch((err) => {
          console.error("Update failed:", err);
        });
    } catch (error) {
      console.error("Error updating avatar:", error);
    } finally {
    }
  }

  useEffect(() => {
    const fetchNonce = async () => {
      try {
        const response = await apiRemote.getNonce(); 
        
        if (response.bp_uploader_nonce) {
          setBpUploaderNonce(response.bp_uploader_nonce);
          console.log('Nonce bp_uploader_nonce fetched:', response.bp_uploader_nonce); // Dùng để debug
        }
        if (response.bp_avatar_cropstore_nonce) {
          setBpAvatarCropstoreNonce(response.bp_avatar_cropstore_nonce);
          console.log('Nonce bp_avatar_cropstore_nonce fetched:', response.bp_avatar_cropstore_nonce); // Dùng để debug
        }
        if (response.bp_delete_avatar_link_nonce) {
          setBpDeleteAvatarLinkNonce(response.bp_delete_avatar_link_nonce);
          console.log('Nonce bp_delete_avatar_link_nonce fetched:', response.bp_delete_avatar_link_nonce); // Dùng để debug
        }
        if (response.has_avatar) {
          setHasAvatar(response.has_avatar);
          console.log('has_avatar fetched:', response.has_avatar); // Dùng để debug
        }
      } catch (error) {
        console.error("Error fetching nonce:", error);
        // alert.error("Error getting security token. Please refresh the page.");
      }
    };
    fetchNonce();
  }, []);

  useEffect(() => {
    if (user?.avatar_url) {
      setAvatar(user?.avatar_url);
    }
  }, [user, setAvatar]);

  const latestEducation = profile?.educator_education?.[0];
  const latestExperience = profile?.educator_work_experience?.find((exp: any) => exp.is_current) || profile?.educator_work_experience?.[0];
  const previousExperience = profile?.educator_work_experience?.find((exp: any) => !exp.is_current);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "white",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        flex: 1,
      }}
    >
      {!profile?.educator_info?.is_first_access && (
        <UserFlowModal hasAvatar={hasAvatar} bpUploaderNonce={bpUploaderNonce} bpAvatarCropstoreNonce={bpAvatarCropstoreNonce} bpDeleteAvatarLinkNonce={bpDeleteAvatarLinkNonce} profile={profile} fetchData={fetchData} />
      )}
      <Paper
          sx={{
            borderRadius: 4,
            boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)",
            p: 5,
            mb: 5,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          
          <Box
            sx={{
              width: 120,
              height: 120,
              position: "relative",
              marginRight: 2,
            }}
          >
            <Avatar
              sx={{
                width: 120,
                height: 120,
                border: "4px solid white",
                boxShadow: "0 0 15px rgba(0,0,0,0.1)",
              }}
              src={avatar}
            />
            <label
              htmlFor="avatar"
              style={{
                position: "absolute",
                bottom: "-2px",
                right: "-2px",
                backgroundColor: "#fff",
                padding: "3px",
                borderRadius: "50%",
                border: "1px solid #ddd",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                width: 40,
                height: 40
              }}
            >
              <CameraAltRounded
                sx={{ fontSize: 30, color: "text.secondary" }}
              />
            </label>
            <input
              type="file"
              id="avatar"
              name="avatar"
              style={{ display: "none" }}
              accept=".jpg, .jpeg, .png"
              onChange={handleFileChange}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h3" fontWeight={700} color="#2c3e50" gutterBottom>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="h5" fontWeight={500} color="primary" sx={{ mb: 2 }}>
              {profile?.educator_info?.headline}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              {profile?.educator_info?.country_name && (
                <>
                  <LocationOn sx={{ color: "text.secondary" }} />
                  <Typography color="text.secondary">{profile?.educator_info?.country_name}</Typography>
                </>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {profile?.educator_info?.is_verified_email && (
                <Chip
                  icon={<Verified />}
                  label="Verified Educator"
                  sx={{
                    bgcolor: "#e9f7ef",
                    color: "#27ae60",
                    fontWeight: 600,
                  }}
                />
              )}
              {/* <Chip
                icon={<EmojiEvents />}
                label="TCC Finalist '24"
                sx={{
                  bgcolor: "#fef5e7",
                  color: "#b8860b",
                  fontWeight: 600,
                }}
              /> */}
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: "#4A90E2",
              "&:hover": { bgcolor: "#357ABD" },
              px: 4,
              py: 1.5,
            }}
            onClick={() => navigate('./edit')}
          >
            Edit
          </Button>
      </Paper>

      <Grid container spacing={5}>
          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: "sticky", top: 40 }}>
              {/* Work Experience */}
              <Card sx={{ mb: 4, borderRadius: 4, boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)" }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: "divider" }}
                  >
                    Work Experience
                  </Typography>
                  {profile?.educator_work_experience?.length > 0 && profile.educator_work_experience.map((exp: any, index: number) => (
                    <Box key={index} sx={{ mb: index < profile.educator_work_experience.length - 1 ? 3 : 0 }}>
                      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <Business sx={{ fontSize: 24, color: "primary.main", mt: 0.5 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {exp.title}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {exp.organization}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(exp.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {exp.is_current ? 'Present' : new Date(exp.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>  

              {/* Education */}
              <Card sx={{ mb: 4, borderRadius: 4, boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)" }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: "divider" }}
                  >
                    Education
                  </Typography>
                  {profile?.educator_education?.length > 0 && profile.educator_education.map((edu: any, index: number) => (
                    <Box key={index} sx={{ mb: index < profile.educator_education.length - 1 ? 3 : 0 }}>
                      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <School sx={{ fontSize: 24, color: "primary.main", mt: 0.5 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {edu.degree} ({edu.field_of_study})
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {edu.university}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {edu.start_year} - {edu.end_year}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>

              {/* Professional Links */}
              {profile?.educator_professional_links?.length > 0 && (
                <Card sx={{ borderRadius: 4, boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: "divider" }}
                    >
                      Professional Links
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        "&:hover": { bgcolor: "#EBF2FA" },
                      }}
                      href={profile.educator_professional_links[0]?.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View LinkedIn Profile
                    </Button>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderBottom: 2,
                borderColor: "divider",
                mb: 4,
              }}
            >
              <Tab label="About & Contributions" sx={{ fontWeight: 600, fontSize: "1.1rem", px: 3, py: 2 }} />
              {/* <Tab label="Marketplace" sx={{ fontWeight: 600, fontSize: "1.1rem", px: 3, py: 2 }} /> */}
              {/* <Tab label="Achievements" sx={{ fontWeight: 600, fontSize: "1.1rem", px: 3, py: 2 }} /> */}
            </Tabs>

            {/* About Tab */}
            {activeTab === 0 && (
              <Box>
                <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      About Me
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      {profile?.educator_professional?.about_me_summary}
                    </Typography>
                  </CardContent>
                </Card>

                {/* <Typography variant="h5" fontWeight={600} sx={{ mb: 3, mt: 5 }}>
                  Recent Contributions
                </Typography>

                <Card sx={{ borderRadius: 3, boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Posted in <strong>#AIinScience</strong> • 2 days ago
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      Just tried using an AI agent to generate differentiated lab reports for my Grade 10 Chemistry class.
                      The time savings were incredible! Has anyone else had success with this?
                    </Typography>
                  </CardContent>
                </Card> */}
              </Box>
            )}

            {/* Marketplace Tab */}
            {activeTab === 1 && (
              <Card sx={{ borderRadius: 3, boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)" }}>
                {/* <CardContent sx={{ p: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Shared a new resource • 1 week ago
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <strong>Resource:</strong> Interactive Periodic Table Lesson Plan (Grade 9-10)
                    <br />
                    <Typography component="span" color="text.secondary">
                      Includes slide deck, student worksheet, and a guide for a hands-on activity.
                    </Typography>
                  </Typography>
                </CardContent> */}
              </Card>
            )}

            {/* Achievements Tab */}
            {activeTab === 2 && (
              <Box>
                {/* <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      🏆 <strong>TCC Finalist '24</strong> - Awarded for an innovative proposal on AI-driven physics
                      simulations.
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: 3, boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      ✓ <strong>Verified Educator</strong> - Verified via official school email address.
                    </Typography>
                  </CardContent>
                </Card> */}
              </Box>
            )}
          </Grid>
      </Grid>
    </Box>
  );
}
